// Visual system shared by the DecisionOpt product landing pages (LineHaulOpt,
// DispatchOpt, StaffOpt and siblings). ExPlan follows it so the products read as
// one family. Light only: none of the family landing pages ship a dark theme.

export const C = {
  bg: '#ffffff',
  band: '#f6f7f9',
  ink: '#12172a',
  body: '#4d505f',
  muted: '#7b8393',
  subtle: '#9aa1ae',
  line: '#e3e7ed',
  lineSoft: '#edf0f4',
  blue: '#0f47b0',
  blueDark: '#0b3789',
  blueSoft: '#e7edf7',
  blueGlow: '#4c86e8',
  teal: '#0e7c74',
  navy: '#12172a',
};

export type TintName = 'green' | 'blue' | 'purple' | 'amber' | 'red' | 'teal';

export const TINTS: Record<TintName, { bg: string; fg: string }> = {
  green: { bg: '#eaf2ed', fg: '#17845a' },
  blue: { bg: '#e7edf7', fg: '#0f47b0' },
  purple: { bg: '#efebf7', fg: '#5b4bc4' },
  amber: { bg: '#f5efe5', fg: '#b5801c' },
  red: { bg: '#f7ebed', fg: '#c0484f' },
  teal: { bg: '#e7f1f0', fg: '#0e7c74' },
};

export const FONT = '"Plus Jakarta Sans", "Inter", -apple-system, "Segoe UI", sans-serif';
export const MONO = '"IBM Plex Mono", ui-monospace, "SFMono-Regular", Menlo, monospace';
/** Product lockup (wordmark + subtitle), set the way ScheduleOpt sets its own. */
export const BRAND_FONT = '"Geist", "Plus Jakarta Sans", -apple-system, "Segoe UI", sans-serif';

/** Navy footer band, matching the ScheduleOpt footer. */
export const NAVY = {
  bg: '#0b1229',
  line: 'rgba(148,163,184,0.14)',
  text: '#c3cede',
  muted: '#8392ab',
  accent: '#6580f2',
};

export const LINKEDIN_URL = 'https://www.linkedin.com/company/decisionopt/';

export const DECISIONOPT_URL = 'https://www.decisionopt.com';
export const CONTACT_EMAIL = 'contact@decisionopt.com';

/*
 * Large screens: the page is zoomed up as a whole, the way ScheduleOpt does it,
 * instead of widening the column. LandingPage sets `--lp-zoom` from these steps
 * (viewport width → zoom) and applies `zoom` to the header, main and footer.
 *
 * Inside zoomed content, `vh` lengths and fixed pixel offsets are scaled by the
 * zoom too, so viewport-height sizes go through zoomVh() and scroll maths use
 * pageZoom() to convert. Keep the steps in sync with ScheduleOpt.
 */
export const PAGE_ZOOM_STEPS: [number, number][] = [
  [1560, 1.1],
  [1760, 1.2],
  [1900, 1.3],
  [2200, 1.5],
  [2560, 1.75],
  [3200, 2.15],
  [3840, 2.6],
];

/** Zoom for a viewport width (window.innerWidth, which is what media queries use). */
export const pageZoom = (viewportWidth: number) =>
  PAGE_ZOOM_STEPS.reduce((zoom, [minWidth, step]) => (viewportWidth >= minWidth ? step : zoom), 1);

/** `--lp-zoom` per breakpoint; spread into the page root's sx. */
export const pageZoomVarsSx = PAGE_ZOOM_STEPS.reduce<Record<string, unknown>>(
  (sx, [minWidth, step]) => ({ ...sx, [`@media (min-width: ${minWidth}px)`]: { '--lp-zoom': step } }),
  { '--lp-zoom': 1 },
);

/** A viewport-height length that stays true inside zoomed content, e.g. zoomVh(100, 72). */
export const zoomVh = (vh: number, minusPx = 0) =>
  `calc(${vh}vh / var(--lp-zoom, 1)${minusPx ? ` - ${minusPx}px` : ''})`;

// Content column, as on ScheduleOpt: 1320px of content plus a side gutter of
// 32px (24px at 1000px and below, 16px at 640px and below).
export const CONTENT_MAX = 1320;

/** Side gutter for a viewport width (window.innerWidth); mirrors wrapSx. */
export const pageGutter = (viewportWidth: number) => (viewportWidth <= 640 ? 16 : viewportWidth <= 1000 ? 24 : 32);

/** Centred content column with ScheduleOpt's side gutter. */
export const wrapSx = {
  width: '100%',
  maxWidth: CONTENT_MAX + 2 * 32,
  mx: 'auto',
  px: '32px',
  boxSizing: 'border-box' as const,
  '@media (max-width: 1000px)': { px: '24px' },
  '@media (max-width: 640px)': { px: '16px' },
};

export const sectionSx = {
  py: 'clamp(64px, 8vw, 104px)',
  scrollMarginTop: '72px',
};

/** Alternate sections sit on the grey band, separated by hairlines. */
export const bandSx = {
  bgcolor: C.band,
  borderTop: `1px solid ${C.lineSoft}`,
  borderBottom: `1px solid ${C.lineSoft}`,
};

export const eyebrowSx = {
  fontFamily: FONT,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.11em',
  textTransform: 'uppercase' as const,
  color: C.blue,
};

export const h2Sx = {
  fontFamily: FONT,
  fontWeight: 800,
  // Matches the Get started headline, the reference size for every section title.
  fontSize: 'clamp(28px, 3vw, 40px)',
  lineHeight: 1.1,
  letterSpacing: '-0.025em',
  color: C.ink,
  textWrap: 'balance' as const,
};

export const ledeSx = {
  fontFamily: FONT,
  fontSize: { xs: 15, md: 16 },
  lineHeight: 1.65,
  color: C.body,
  textWrap: 'pretty' as const,
};

export const cellTitleSx = {
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: 17,
  lineHeight: 1.3,
  letterSpacing: '-0.01em',
  color: C.ink,
};

export const cellBodySx = {
  fontFamily: FONT,
  fontSize: 14,
  lineHeight: 1.6,
  color: C.body,
};

/** Small uppercase key above a cell title, e.g. "UTILISATION". */
export const kickerSx = {
  fontFamily: FONT,
  fontSize: 11.5,
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase' as const,
  color: C.muted,
};

const btnBaseSx = {
  borderRadius: 999,
  textTransform: 'none' as const,
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: 15.5,
  height: 52,
  px: '30px',
  boxShadow: 'none',
  whiteSpace: 'nowrap' as const,
  '&:hover': { boxShadow: 'none' },
  '&:focus-visible': { outline: `2px solid ${C.blue}`, outlineOffset: 2 },
};

export const btnPrimarySx = {
  ...btnBaseSx,
  bgcolor: C.blue,
  color: '#ffffff',
  '&:hover': { bgcolor: C.blueDark, boxShadow: 'none' },
};

export const btnOutlineSx = {
  ...btnBaseSx,
  bgcolor: C.bg,
  color: C.ink,
  border: `1px solid ${C.line}`,
  '&:hover': { bgcolor: C.bg, borderColor: C.subtle, boxShadow: 'none' },
};

export const btnSmallSx = { height: 44, px: '22px', fontSize: 14.5 };

/**
 * Bordered box whose cells are divided by hairlines. The 1px gap shows the line
 * colour through, so dividers stay correct at every column count.
 */
export const hairlineGridSx = (columns: { xs?: number; sm?: number; md?: number }) => ({
  display: 'grid',
  gridTemplateColumns: {
    xs: `repeat(${columns.xs ?? 1}, minmax(0, 1fr))`,
    sm: `repeat(${columns.sm ?? columns.xs ?? 1}, minmax(0, 1fr))`,
    md: `repeat(${columns.md ?? columns.sm ?? columns.xs ?? 1}, minmax(0, 1fr))`,
  },
  gap: '1px',
  bgcolor: C.line,
  border: `1px solid ${C.line}`,
  borderRadius: '14px',
  overflow: 'hidden',
});

export const hairlineCellSx = {
  bgcolor: C.bg,
  p: { xs: 3, md: 3.5 },
  minWidth: 0,
};

export const chipSx = (tint: TintName, size = 38) => ({
  width: size,
  height: size,
  borderRadius: '10px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  bgcolor: TINTS[tint].bg,
  color: TINTS[tint].fg,
});

/**
 * Solid icon tile: royal-to-indigo gradient with a white icon, the way the
 * ScheduleOpt pillar cards draw theirs. Used for the gap and capability cards.
 */
export const iconTileSx = (size = 52) => ({
  width: size,
  height: size,
  borderRadius: '14px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: '#ffffff',
  background: 'linear-gradient(160deg, #2f6bf0 0%, #1c2b9a 100%)',
  boxShadow: '0 8px 18px -8px rgba(28,43,154,0.55)',
});
